import { html, render, TemplateResult } from 'lit';
import { csvToJson } from '../components/csv-to-json';
import { getWERSRatingResults } from '../components/ratings';
import { fetchCSV } from '../data/search-data';
import { PageResult, RequestPage, TableView } from './table-view';
import { ViewBase } from './view-base';
import { hash, pageNames } from './view-consts';

function clone<ItemType>(item: ItemType): ItemType {
  return globalThis.structuredClone(item);
}

/*this lets us easily bypass the typescript jquery definitions which are not what we want to use for datatables as the
typescript is out of date.
DataTables must be added to the main page directly, not compiled in*/
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const $j = $.fn as any;

interface Film {
  windowId: string;
  manufacturer: string;
  glazingId: string;
  frameDesc: string;
  uval: string;
  shgc: string;
  tv: string;
  ai: string;
  glazingDesc: string;
}

export interface FilmTableFilter {
  windowId?: string;
  frameDescriptionKeyword?: string;
  uwMin?: number;
  uwMax?: number;
  shgc?: number;
  shgcPlus?: number;
  shgcMinus?: number;
  minTvw?: number;
  manufacturerCode?: string;
}
class FilmTable extends TableView<Film> {
  data: Film[] | null = null;
  filter: FilmTableFilter = {};
  onDataReloaded?: () => void;
  source(): string {
    return '/api/wers/data/commercial_film.csv';
  }

  filteredData(): Film[] {
    function percentOf(value: number, perc?: number) {
      if (value === 0) return 0;
      if (!perc) return 0;
      return (value / 100) * perc;
    }

    const f = this.filter;
    if (!f) return this.data ?? [];
    const windowId = (f.windowId ?? '').toLowerCase();
    const frameKey = (f.frameDescriptionKeyword ?? '').toLowerCase();
    const checkSHGC = f.shgc;
    const shgc = f.shgc ?? 0;
    const lowSHGC = shgc - percentOf(shgc, f.shgcMinus);
    const highSHGC = shgc + percentOf(shgc, f.shgcPlus);
    const manufacturer =
      f.manufacturerCode && f.manufacturerCode !== ''
        ? f.manufacturerCode?.toLowerCase().split('|') ?? []
        : [];

    return (this.data ?? []).filter((item) => {
      let uVal = parseFloat(item.uval);
      let shgc = parseFloat(item.shgc);
      let tvw = parseFloat(item.tv);

      if (windowId != '' && !item.windowId.toLowerCase().startsWith(windowId))
        return false;
      if (frameKey !== '' && !item.frameDesc.toLowerCase().includes(frameKey))
        return false;
      if (!isNaN(uVal) && f.uwMin !== undefined && uVal < f.uwMin) return false;
      if (!isNaN(uVal) && f.uwMax !== undefined && uVal > f.uwMax) return false;
      if (checkSHGC && !isNaN(uVal) && (shgc < lowSHGC || shgc > highSHGC))
        return false;
      if (!isNaN(tvw) && f.minTvw !== undefined && tvw < f.minTvw) return false;
      if (
        manufacturer.length > 0 &&
        !manufacturer.includes(item.manufacturer.toLowerCase())
      )
        return false;
      return true;
    });
  }
  async getRowsFromServer(request: RequestPage): Promise<PageResult<Film>> {
    if (!this.data) {
      this.data = (await fetchCSV(this.source())) as Film[];
      this.onDataReloaded?.();
    }
    const data = this.filteredData();
    return {
      count: data.length,
      pageCount: this.pageLength(),
      pageIndex: request.pageIndex,
      pageSize: request.pageSize,
      results: data.slice(
        request.pageIndex * request.pageSize,
        request.pageIndex * request.pageSize + this.pageLength()
      ),
    };
  }
  getColumns(): any[] {
    return [
      {
        title: 'Window ID',
        width: '100px',
        data: 'windowId',
      },
      {
        title: 'Manufacturer',
        width: '100px',
        data: 'manufacturer',
      },
      {
        title: 'Frame',
        width: '600px',
        data: 'frameDesc',
      },
      {
        title: 'Glazing',
        width: '120px',
        data: 'glazingId',
        render: (value: string, _type: never, row: Film) => {
          return `<span class="text-primary" data-bs-toggle="tooltip" data-bs-placement="top" title="${row.glazingDesc}"><u>${value}</u></span>`;
        },
      },
      /*
            {
                title: "Cooling Stars",
                width: "100px",
                data: "skylightId",
                render: (value: string, _type: never, row: Film) => {
                    const r = getWERSRatingResults(parseFloat(row.uval), parseFloat(row.shgc), "");
                    return `<wers-stars value="${r.coolingStars ?? 0}" type="cool"></wers-stars>`;

                }
            },
            {
                title: "Heating Stars",
                width: "100px",
                data: "skylightId",
                render: (value: string, _type: never, row: Film) => {
                    const r = getWERSRatingResults(parseFloat(row.uval), parseFloat(row.shgc), "");
                    return `<wers-stars value="${r.heatingStars ?? 0}" type="heat"></wers-stars>`;

                }

            },
                */
      {
        title: 'Cooling %',
        width: '100px',
        data: 'skylightId',
        render: (value: string, _type: never, row: Film) => {
          const r = getWERSRatingResults(
            parseFloat(row.uval),
            parseFloat(row.shgc),
            ''
          );
          return `<span class="text-primary">${
            r.coolingPercentage ?? 0
          }</span>`;
        },
      },
      {
        title: 'Heating %',
        width: '100px',
        data: 'skylightId',
        render: (value: string, _type: never, row: Film) => {
          const r = getWERSRatingResults(
            parseFloat(row.uval),
            parseFloat(row.shgc),
            ''
          );
          return `<span class="text-danger">${r.heatingPercentage ?? 0}</span>`;
        },
      },

      {
        title: 'Uw',
        width: '100px',
        data: 'uval',
        render: (value: string, _type: never, row: Film) => {
          return parseFloat(value).toFixed(1);
        },
      },
      {
        title: 'SHGCw',
        width: '100px',
        data: 'shgc',
        render: (value: string, _type: never, row: Film) => {
          return parseFloat(value).toFixed(2);
        },
      },
      {
        title: 'Tvw',
        width: '100px',
        data: 'tv',
        render: (value: string, _type: never, row: Film) => {
          return parseFloat(value).toFixed(2);
        },
      },
      {
        title: 'ai',
        width: '100px',
        data: 'ai',
        render: (value: string, _type: never, row: Film) => {
          return parseFloat(value).toFixed(2);
        },
      },
    ];
  }
}

class CommercialFilmTable extends FilmTable {
  source(): string {
    return '/api/wers/data/commercial_film.csv';
  }
}

class ResidentialFilmTable extends FilmTable {
  source(): string {
    return '/api/wers/data/residential_film.csv';
  }
}
const tabs = {
  commercial: 'commercial-film',
  residential: 'residential-film',
};

export class FilmsView extends ViewBase {
  private activeTab: string = tabs.commercial;
  private residentialTable: ResidentialFilmTable;
  private commercialTable: CommercialFilmTable;
  private elementId: string;
  changeEvent: number = 0;
  constructor(name: string, globalRenderEvent: () => Promise<void>) {
    super(name, globalRenderEvent);
    this.residentialTable = new ResidentialFilmTable();
    this.commercialTable = new CommercialFilmTable();
    this.residentialTable.onDataReloaded = () => this.updateFilter();
    this.commercialTable.onDataReloaded = () => this.updateFilter();
    this.elementId = `filmsview-${this.uiid}`;
  }
  updateFilter(): void {
    this.render();
  }
  protected doConstruct(): void {
    super.doConstruct();
  }

  protected async onPageActivated(active: Boolean) {
    this.residentialTable.render();
    this.commercialTable.render();
  }
  protected $(): any {
    return $(`#${this.elementId}`);
  }
  async clearSelect(arg0: HTMLSelectElement) {
    ($(arg0) as any).select2('val', '-1');
  }
  setListValues(parent: JQuery<HTMLElement>, name: string, selection: string) {
    const sel = selection.split('|');
    (parent.find(`#input-${name}`) as any).val(sel).change();
  }
  getListValues(parent: JQuery<HTMLElement>, name: string): string {
    const values = (parent.find(`#input-${name}`) as any).select2('val');
    //this should not occur in a proper setup, but fine during development when things are inprogress
    if (!values) return '';
    return values.join('|');
  }
  protected async onAfterRender() {
    const jthis = this.$();

    try {
      jthis
        .find('.use-select2')
        .each((index: number, elem: HTMLSelectElement) => {
          this.select2(elem, { multiple: true });
          this.clearSelect(elem);
          ($(this) as any).val([]).change();
        });

      this.setListValues(
        this.$(),
        'manufacturer-residential',
        this.residentialTable.filter.manufacturerCode ?? ''
      );
      this.setListValues(
        this.$(),
        'manufacturer-commercial',
        this.commercialTable.filter.manufacturerCode ?? ''
      );
      this.$();
    } finally {
    }
  }

  public async template(): Promise<TemplateResult> {
    const date = new Date();
    const isActive = (name: string, trueVal: string, falseVal: string = '') => {
      return this.activeTab === name ? trueVal : falseVal;
    };
    const goResidential = () => this.setActiveTab(tabs.residential);
    const goCommercial = () => this.setActiveTab(tabs.commercial);
    return html`
      <div id=${this.elementId} class="text-left">
        <h1 class="">Films</h1>
        <ul class="nav nav-tabs" id="film-tabs" role="tablist">
          <li
            class="nav-item"
            role="presentation"
            id=${tabs.commercial + '-tab'}
          >
            <button
              class=${'nav-link ' + isActive(tabs.commercial, ' active')}
              @click=${goCommercial}
              type="button"
              role="tab"
              aria-controls=${tabs.commercial + '-data'}
              aria-selected=${isActive(tabs.commercial, 'true')}
            >
              Commercial
            </button>
          </li>
          <li
            class="nav-item"
            role="presentation"
            id=${tabs.residential + '-tab'}
          >
            <button
              class=${'nav-link ' + isActive(tabs.residential, ' active')}
              @click=${goResidential}
              type="button"
              role="tab"
              aria-controls=${tabs.residential + '-data'}
              aria-selected=${isActive(tabs.commercial, 'true')}
            >
              Residential
            </button>
          </li>
        </ul>
        <div class="tab-content">
          <div
            class=${'tab-pane ' + isActive(tabs.commercial, ' show active')}
            id=${tabs.commercial + '-data'}
            role="tabpanel"
            aria-labelledby=${tabs.commercial + '-tab'}
          >
            <div class="row">
              <div class="col-3">${this.commercialFilter()}</div>
              <div class="col-9">${this.commercialTable.ui}</div>
            </div>
          </div>
          <div
            class=${'tab-pane ' + isActive(tabs.residential, ' show active')}
            id=${tabs.residential + '-data'}
            role="tabpanel"
            aria-labelledby=${tabs.residential + '-tab'}
          >
            <div class="row">
              <div class="col-3">${this.residentialFilter()}</div>
              <div class="col-9">${this.residentialTable.ui}</div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  sidebar(table: FilmTable, name: string) {
    const filter = clone(table.filter);

    const eventWindowId = (e: Event) => {
      const value = (e.target as HTMLInputElement).value?.trim() ?? '';
      filter.windowId = value === '' ? undefined : value;
    };
    const eventFrameKeyword = (e: Event) => {
      const value = (e.target as HTMLInputElement).value?.trim() ?? '';
      filter.frameDescriptionKeyword = value === '' ? undefined : value;
    };
    const eventNumber = (e: Event) => {
      const value = (e.target as HTMLInputElement).value?.trim() ?? '';
      const num = value === '' ? undefined : parseFloat(value);
      return num !== undefined && !isNaN(num) ? num : undefined;
    };
    const eventUwMin = (e: Event) => {
      filter.uwMin = eventNumber(e);
    };
    const eventUwMax = (e: Event) => {
      filter.uwMax = eventNumber(e);
    };
    const eventSHGC = (e: Event) => {
      filter.shgc = eventNumber(e);
    };
    const eventSHGCPlus = (e: Event) => {
      filter.shgcPlus = eventNumber(e);
    };
    const eventSHGCMinus = (e: Event) => {
      filter.shgcMinus = eventNumber(e);
    };
    const eventTvw = (e: Event) => {
      filter.minTvw = eventNumber(e);
    };
    const eventResetFilter = () => {
      table.filter = {};
      table.refreshData();
    };
    const eventApplyFilter = () => {
      filter.manufacturerCode = this.getListValues(
        this.$(),
        `manufacturer-${name}`
      );
      table.filter = clone(filter);
      table.refreshData();
    };

    const manufacturerTemplate = (): TemplateResult => {
      const input = 'manufacturer';
      const label = 'Manufacturer';
      const items: string[] = [];
      table.data?.forEach((element) => {
        const m = element.manufacturer;
        if (!items.includes(m)) items.push(m);
      });
      const mFilter = (filter.manufacturerCode ?? '').split('|');
      const isSelected = (id: string) => mFilter.includes(id);

      let clearEvent = async (e: Event) => {
        const selector = (e.target as HTMLButtonElement)
          .closest('div')
          ?.querySelector('select');
        if (selector) ($(selector) as any).select2('val', '-1');
        filter.manufacturerCode = '';
        await this.render();
      };
      const eventChange = (e: Event) => {
        if (e.target) {
          const val = ($(e.target) as any).select2('val');
          filter.manufacturerCode = (val as string[]).join('|');
        }
      };
      return html` <div class="input-group input-group-sm mb-1">
        <button
          class="btn btn-outline-secondary"
          @click=${clearEvent}
          type="button"
          id="button-addon2"
        >
          <i class="fas fa-times"></i>
        </button>
        <select
          class="form-select use-select2"
          id=${`input-manufacturer-${name}`}
          data-placeholder=${label}
          mutiple
          aria-label=${label}
          @change=${eventChange}
        >
          ${items?.map(
            (m) => new Option(m, m, false, isSelected && isSelected(m))
          )}
        </select>
      </div>`;
    };

    return html` <div id="filter" class="ms-3">
      <div class="sidebar-filters">
        <div class="row mb-2">
          <div class="col-12">
            <label for="input-windowId" class="form-label mx-2 my-0"
              >Window ID Starts with</label
            >
            <input
              id="input-windowId"
              type="search"
              .value=${filter.windowId ?? ''}
              @change=${eventWindowId}
            />
          </div>
        </div>
        <div class="row mb-2">
          <div class="col-12">
            <label
              for="input-frameDescriptionFilter"
              class="form-label mx-2 my-0"
              >Frame Description Keyword Search</label
            >
            <input
              id="input-frameDescriptionFilter"
              type="search"
              .value=${filter.frameDescriptionKeyword ?? ''}
              @change=${eventFrameKeyword}
            />
          </div>
        </div>
        <div class="row mb-2">
          <div class="col-12">
            <label for="input-min-uvalue" class="form-label">U-Value Min</label>
            <input
              id="input-min-uvalue"
              type="number"
              min="0"
              max="7"
              .value=${filter.uwMin ?? ''}
              @change=${eventUwMin}
            />
            <label for="input-max-uvalue" class="form-label">Max</label>
            <input
              id="input-max-uvalue"
              type="number"
              min="0"
              max="7"
              .value=${filter.uwMax ?? ''}
              @change=${eventUwMax}
            />
            <div class="form-text">
              please provide a number from <b>0 to 7</b> to filter your search,
              or leave blank
            </div>
          </div>
        </div>
        <div class="row mb-2">
          <div class="col-12">
            <label for="input-shgc" class="form-label">SHGCw</label>
            <div class="input-group shgc-group mb-3">
              <input
                id="input-shgc"
                type="number"
                min="0"
                max="1"
                class="form-control"
                placeholder="SHGCw Value"
                aria-label="SHGCw Value"
                .value=${filter.shgc ?? ''}
                @change=${eventSHGC}
              />
              <span class="input-group-text">%-</span>
              <input
                id="input-shgc-perc-minus"
                type="number"
                class="form-control"
                aria-label="minus percentage"
                min="0"
                max="100"
                .value=${filter.shgcMinus ?? ''}
                @change=${eventSHGCMinus}
              />
              <span class="input-group-text">%+</span>
              <input
                id="input-shgc-perc-plus"
                type="number"
                class="form-control"
                aria-label="plus percentage"
                min="0"
                max="100"
                .value=${filter.shgcPlus ?? ''}
                @change=${eventSHGCPlus}
              />
            </div>
            <div class="form-text">
              please provide an SGHC number from <b>0 - 1</b> along with a %
              range, or leave blank
            </div>
          </div>
        </div>
        <div class="row mb-2">
          <div class="col-12">
            <label for="input-min-tvw" class="form-label">Minimum Tvw</label>
            <input
              id="input-min-tvw"
              type="number"
              min="0"
              max="1"
              .value=${filter.minTvw ?? ''}
              @change=${eventTvw}
            />
            <div class="form-text">
              please provide a Tvw from <b>0 to 1</b> to filter, or leave blank
              to ignore
            </div>
          </div>
        </div>
      </div>
      <div class="state-filter">
        <div class="row">
          <div class="col-12">
            <div class="my-2">Filter Manufacturer</div>
            ${manufacturerTemplate()}
          </div>
        </div>
      </div>
      <div class="button-section">
        <div class="row mb-2">
          <div class="col">
            <button
              data-bs-toggle="collapse"
              data-bs-target="#section-filter"
              class="btn btn-sm btn-primary"
              @click=${eventApplyFilter}
            >
              Search
            </button>
            <button class="btn btn-sm btn-secondary" @click=${eventResetFilter}>
              Clear Search
            </button>
          </div>
        </div>
      </div>
    </div>`;
  }
  residentialFilter(): unknown {
    return this.sidebar(this.residentialTable, 'residential');
  }
  commercialFilter(): unknown {
    return this.sidebar(this.commercialTable, 'commercial');
  }
  setActiveTab(tabName: string) {
    this.activeTab = tabName;
    this.render();
  }
}
